import {
  ExpressionModeKey,
  FunnelModeKey,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { IDisplayedSlice } from '../../interfaces';
import { useAppDispatch } from '../../../../redux/hooks';
import prepareCachedCohort from '../../../../utils/createCohort/prepareCachedCohort';
import { useAnalysisPrepareCohort } from '../../../../utils/createCohort';
import { saveCachedCohortAndOpenModalThunk } from '../../../Builder/Cohorts/CohortRow/utils';
import { MaybeNil } from '../../../../utils/interfaces';
import {
  parseCohortDurationStartDate,
  parseFunnelLookForwardWindow,
} from '../../../../redux/slices/oldBuilder/utils';
import {
  ICohortDurationStart,
  ILookForwardWindow,
} from '../../../../redux/slices/oldBuilder';
import { assertCondition } from '../../../../utils/types';
import { findMeasureIdByMeasureKeyInBuilderInfo } from '../../../Builder/utils/builderUtils';
import { getCohortPagePath } from '../../../../utils';
import { useNavigate } from 'react-router-dom';
import { useChartContext } from '../../ChartContext';

interface IProps {
  slice: IDisplayedSlice;
  type: SimpleCohortTypeKey;
  isExecuteAfterRedirect: boolean;
}

type IReturn = (props: IProps) => Promise<void>;

export default (analysisId: string): IReturn => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    metadata: { query, startDate, endDate },
    settings: { chartSettings },
  } = useChartContext();
  const { displayedMeasureSettings } = chartSettings;
  const analysisPrepareCohort = useAnalysisPrepareCohort(analysisId);

  return async ({ slice, type, isExecuteAfterRedirect }: IProps) => {
    let measureId;
    if (query?.__typename === 'ExpressionQuery') {
      assertCondition(displayedMeasureSettings.length > 0);
      measureId = findMeasureIdByMeasureKeyInBuilderInfo(
        query,
        displayedMeasureSettings[0].key,
      );
    }
    const response = await analysisPrepareCohort(slice.key, measureId);

    const analysis = response.data?.analysisPrepareCohort?.entity?.analysis;
    const newMeasureId =
      response.data?.analysisPrepareCohort?.entity?.expressionMeasure?.id;
    if (!analysis) {
      return;
    }

    let histogramBucket: MaybeNil<string> = null;
    if (
      query?.__typename === 'ExpressionQuery' &&
      query.expressionMode.key === ExpressionModeKey.Histogram
    ) {
      histogramBucket = slice.originalHint[0].value;
    }

    let cohortDurationStart: ICohortDurationStart | undefined;
    let lookForwardWindow: ILookForwardWindow | undefined;
    if (
      query?.__typename === 'FunnelQuery' ||
      query?.__typename === 'RetentionQuery'
    ) {
      cohortDurationStart = parseCohortDurationStartDate(query, startDate);
    }

    if (query?.__typename === 'FunnelQuery') {
      lookForwardWindow = parseFunnelLookForwardWindow(query, null);
    }

    const isTimeToConvertMode =
      query?.__typename === 'FunnelQuery' &&
      query.mode?.key === FunnelModeKey.TimeToConvert;

    const cachedCohortData = prepareCachedCohort({
      analysisId: analysis.id,
      measureId: newMeasureId,
      referToNewMeasure: !!newMeasureId,
      type,
      histogramBucket,
      startDate,
      endDate,
      builderInfo: query,
      isExecuteAfterRedirect,
      // in funnel chart, we don't know which step is selected on click chart group,
      // therefore set it as default step 0.
      index: type === SimpleCohortTypeKey.Funnel ? 0 : undefined,
      cohortDurationStart,
      lookForwardWindow,
      timeToConvertInput: isTimeToConvertMode
        ? {
            operator: TimeToConvertOperatorKey.GreaterThan,
            timeUnit: TimeUnit.Day,
            values: [0],
          }
        : null,
    });
    dispatch(
      saveCachedCohortAndOpenModalThunk(
        cachedCohortData,
        isExecuteAfterRedirect,
        analysisId,
      ),
    );

    if (isExecuteAfterRedirect) {
      navigate(getCohortPagePath());
    }
  };
};
